import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRightLeft, CalendarDays, ClipboardCheck, Image as ImageIcon, Pencil, RotateCcw, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDemo } from "@/store/DemoStore";
import { api } from "@/lib/api";
import { Avatar, Card, CardTitle, StatusBadge, btn } from "@/components/common";
import { ChatThread } from "@/components/ChatThread";
import { FactList, InvoicePanel, InvoiceTotals, ItineraryTimeline } from "@/components/TripSections";
import { InvoiceBuilder } from "@/components/InvoiceBuilder";
import { ItineraryEditor } from "@/components/ItineraryEditor";
import { PaymentPlanEditor } from "@/components/PaymentPlanEditor";
import { NewQuoteDialog } from "@/components/NewQuoteDialog";
import { AuthorizationsPanel } from "@/components/PaymentAuthorizations";
import { TripDocuments } from "@/components/TripDocuments";
import { cn } from "@/lib/utils";
import { daysUntil, fmtDate, fmtMoney, nextPayment, returnDate, tripCommission } from "@/lib/trip";
import { CommissionCard } from "@/components/CommissionCard";

const TABS = [["overview", "Overview"], ["itinerary", "Itinerary"], ["invoice", "Invoice"], ["documents", "Documents"], ["messages", "Messages"]];

function compressImage(file, max = 1280, quality = 0.72) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("load failed")); };
    img.src = url;
  });
}

export function ConvertButton({ trip, className }) {
  const { convert } = useDemo();
  if (trip.status !== "QUOTE") return null;
  return (
    <button data-testid="convert-quote-to-trip-btn" className={cn(btn.primary, className)} onClick={() => { convert(trip.id); toast.success("Converted to trip", { description: `${trip.quoteNumber} is now BOOKED.` }); }}>
      <ArrowRightLeft className="h-4 w-4" />Convert to Trip
    </button>
  );
}

function Hero({ trip, client, onEdit, onArchive, onRestore, onDelete }) {
  const { updateTrip } = useDemo();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const d = daysUntil(trip.departure);
  const pill = "inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-white backdrop-blur hover:bg-white/20";
  const copySurveyLink = async () => {
    try {
      const { data } = await api.post(`/trips/${trip.id}/survey-link`);
      try { await navigator.clipboard.writeText(data.link); toast.success("Survey link copied", { description: "Paste it to your traveler to collect post-trip feedback." }); }
      catch { toast.success("Survey link ready", { description: data.link }); }
    } catch { toast.error("Couldn't create a survey link"); }
  };
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    setBusy(true);
    try {
      const dataUrl = await compressImage(file);
      await updateTrip(trip.id, { image: dataUrl });
      toast.success("Cover photo updated", { description: "Your client sees it on their booking too." });
    } catch { toast.error("Couldn't process that image"); }
    setBusy(false);
  };
  return (
    <div className="relative -mx-4 -mt-6 overflow-hidden sm:mx-0 sm:mt-0 sm:rounded-3xl">
      <img src={trip.image || "/images/sunset.jpg"} alt={trip.destination} className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-navy-950/50 to-navy-950/20" />
      <div className="relative flex min-h-[220px] flex-col justify-between gap-6 p-5 sm:min-h-[260px] sm:p-8">
        <div className="flex items-center justify-between gap-3">
          <Link to="/agent/trips" data-testid="back-to-trips" className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-white backdrop-blur hover:bg-white/20"><ArrowLeft className="h-3.5 w-3.5" />All Trips</Link>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {!trip.archived && <>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPick} data-testid="trip-cover-input" />
              <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="trip-change-cover-btn" className={cn(pill, busy && "opacity-60")}><ImageIcon className="h-3.5 w-3.5" />{busy ? "Uploading…" : "Change Cover"}</button>
              <button onClick={onEdit} data-testid="trip-edit-details-btn" className={pill}><Pencil className="h-3.5 w-3.5" />Edit Details</button>
              {trip.status !== "QUOTE" && <button onClick={copySurveyLink} data-testid="trip-survey-link-btn" className={pill}><ClipboardCheck className="h-3.5 w-3.5" />Survey Link</button>}
            </>}
            {trip.archived ? (
              <>
                <button onClick={onRestore} data-testid="trip-restore-btn" className={pill}><RotateCcw className="h-3.5 w-3.5" />Restore</button>
                <button onClick={onDelete} data-testid="trip-delete-btn" className="inline-flex items-center gap-1.5 rounded-full bg-red-500/25 px-3 py-1.5 text-xs font-medium text-red-100 backdrop-blur hover:bg-red-500/40"><Trash2 className="h-3.5 w-3.5" />Delete</button>
              </>
            ) : (
              <button onClick={onArchive} data-testid="trip-archive-btn" className="inline-flex items-center gap-1.5 rounded-full bg-red-500/25 px-3 py-1.5 text-xs font-medium text-red-100 backdrop-blur hover:bg-red-500/40"><Trash2 className="h-3.5 w-3.5" />Delete</button>
            )}
            {trip.archived ? <span className="rounded-md bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-600" data-testid="trip-archived-badge">Archived</span> : <StatusBadge status={trip.status} className="bg-white" />}
          </div>
        </div>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0 text-white">
            <Link to={`/agent/clients/${client.id}`} className="text-sm font-medium text-white/70 hover:text-white" data-testid="trip-client-link">{client.name}</Link>
            <h1 className="mt-1 font-display text-2xl font-bold tracking-tight sm:text-4xl" data-testid="trip-title">{trip.title}</h1>
            <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/75">
              <span>{trip.vessel}</span>
              <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(trip.departure)} – {fmtDate(returnDate(trip))}</span>
              <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{trip.travelers.length} travelers</span>
            </p>
          </div>
          {trip.status === "QUOTE" ? <ConvertButton trip={trip} className="bg-white text-navy-900 hover:bg-slate-100" /> : d >= 0 && (
            <div className="text-white sm:text-right"><p className="font-display text-3xl font-bold leading-none">{d}</p><p className="text-xs text-white/60">days until departure</p></div>
          )}
        </div>
      </div>
    </div>
  );
}

function Overview({ trip, settings }) {
  const { paidExtra } = useDemo();
  const extra = paidExtra(trip.id);
  const np = nextPayment(trip, extra);
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
      <Card><CardTitle>Trip Details</CardTitle><div className="p-5 sm:p-6"><FactList trip={trip} /></div></Card>
      <div className="space-y-5">
        <Card><CardTitle>Payment</CardTitle><div className="p-5 sm:p-6"><InvoiceTotals trip={trip} extraPaid={extra} />
          {np && <p className="mt-4 rounded-xl bg-amber-50 px-3.5 py-2.5 text-xs text-amber-800" data-testid="next-payment-note">{np.label} of {fmtMoney(np.outstanding ?? np.amount)} due {fmtDate(np.due)}</p>}
        </div></Card>
        <Card><CardTitle>Commission <span className="ml-1 normal-case tracking-normal text-amber-600">internal</span></CardTitle><div className="flex items-baseline justify-between p-5 sm:p-6"><span className="text-sm text-slate-500">Agent payout {settings.agentSplit}% · {fmtMoney(tripCommission(trip, settings.commissionRate) * settings.agentSplit / 100)}</span><span className="font-display text-xl font-bold text-navy-900">{fmtMoney(tripCommission(trip, settings.commissionRate))}</span></div></Card>
      </div>
      <Card><CardTitle>Travelers</CardTitle>
        <ul className="space-y-3 p-5 sm:p-6">{trip.travelers.map((n, i) => (
          <li key={n} className="flex items-center gap-3"><Avatar name={n} size="sm" /><span className="flex-1 text-sm font-medium text-navy-900">{n}</span>{i === 0 && <span className="rounded-md bg-navy-900 px-2 py-0.5 text-[10px] font-semibold text-white">PRIMARY</span>}</li>
        ))}</ul>
      </Card>
    </div>
  );
}

function TripMessages({ trip }) {
  const { convFor, send, markRead } = useDemo();
  const conv = convFor(trip.clientId);
  const len = conv?.messages.length;
  useEffect(() => { if (conv?.unreadAgent) markRead(conv.id, "agent"); }, [conv?.id, len]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Card className="h-[min(600px,calc(var(--app-h,100dvh)-220px))] overflow-hidden" data-testid="trip-messages-panel">
      <ChatThread messages={conv?.messages || []} viewer="agent" readAt={conv?.clientReadAt} onSend={(t) => send(trip.clientId, "agent", t, trip.id)} placeholder="Reply to client…" />
    </Card>
  );
}

export default function TripWorkspace() {
  const { id } = useParams();
  const { trip: get, client, settings, archiveTrip, restoreTrip, deleteTrip, authorizations = [] } = useDemo();
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const nav = useNavigate();
  const trip = get(id);
  if (!trip) return <Navigate to="/agent/trips" replace />;
  const tab = params.get("tab") || "overview";
  const paidExtra = authorizations.filter((a) => a.tripId === trip.id && a.status === "COMPLETE").reduce((s, a) => s + a.amount, 0);
  const done = () => setEditing(null);
  const doConfirm = async () => {
    const r = confirm === "delete" ? await deleteTrip(trip.id) : await archiveTrip(trip.id);
    setConfirm(null);
    if (r) nav("/agent/trips");
  };

  return (
    <div data-testid="trip-workspace">
      <Hero trip={trip} client={client(trip.clientId)} onEdit={() => setEditing("details")} onArchive={() => setConfirm("archive")} onRestore={() => restoreTrip(trip.id)} onDelete={() => setConfirm("delete")} />
      <div className="no-scrollbar sticky top-0 z-10 -mx-4 mb-6 mt-4 flex gap-1 overflow-x-auto border-b border-slate-200 bg-[#F5F7FA] px-4 sm:mx-0 sm:px-0">
        {TABS.map(([v, l]) => (
          <button key={v} data-testid={`trip-tab-${v}`} onClick={() => { setEditing(null); setParams({ tab: v }, { replace: true }); }} className={cn("-mb-px whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors", tab === v ? "border-navy-900 text-navy-900" : "border-transparent text-slate-500 hover:text-navy-900")}>{l}</button>
        ))}
      </div>
      {tab === "overview" && <Overview trip={trip} settings={settings} />}
      {tab === "itinerary" && (
        <div className="mx-auto max-w-3xl">
          {editing === "itinerary" ? <Card className="p-5 sm:p-6"><ItineraryEditor trip={trip} withEvents onDone={done} /></Card> : (
            <><div className="mb-4 flex justify-end"><button onClick={() => setEditing("itinerary")} data-testid="trip-edit-itinerary-btn" className={btn.secondary}><Pencil className="h-4 w-4" />Edit Itinerary</button></div><ItineraryTimeline trip={trip} /></>
          )}
        </div>
      )}
      {tab === "invoice" && (
        <div>
          {!editing && (
            <div className="mb-4 flex flex-wrap justify-end gap-2">
              <button onClick={() => setEditing("invoice")} data-testid="trip-edit-invoice-btn" className={btn.secondary}><Pencil className="h-4 w-4" />Edit Invoice</button>
              <button onClick={() => setEditing("plan")} data-testid="trip-edit-plan-btn" className={btn.secondary}><Pencil className="h-4 w-4" />Edit Payment Plan</button>
            </div>
          )}
          {editing === "invoice" ? <Card><CardTitle>Edit Invoice</CardTitle><div className="p-5 sm:p-6"><InvoiceBuilder trip={trip} onDone={done} /></div></Card>
            : editing === "plan" ? <Card className="max-w-xl"><CardTitle>Edit Payment Plan</CardTitle><div className="p-5 sm:p-6"><PaymentPlanEditor trip={trip} onDone={done} /></div></Card>
            : <InvoicePanel trip={trip} extraPaid={paidExtra} extra={<><CommissionCard key={trip.commission ?? "auto"} trip={trip} /><AuthorizationsPanel tripId={trip.id} canComplete /></>} />}
        </div>
      )}
      {tab === "documents" && <TripDocuments trip={trip} canDelete />}
      {editing === "details" && <NewQuoteDialog open trip={trip} onOpenChange={(o) => !o && done()} />}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-2xl p-6 sm:p-7" data-testid="trip-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-navy-900">{confirm === "delete" ? "Delete Permanently?" : "Delete This Trip?"}</DialogTitle>
            <DialogDescription>{confirm === "delete" ? "This permanently removes the trip and can't be undone." : "It moves to Archived and drops off the client's trips and the main list. You can restore it anytime from the Archived filter."}</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <button onClick={() => setConfirm(null)} className={btn.secondary}>Cancel</button>
            <button onClick={doConfirm} className={cn(btn.primary, "bg-red-600 hover:bg-red-700")} data-testid="trip-confirm-btn">{confirm === "delete" ? "Delete Permanently" : "Delete Trip"}</button>
          </div>
        </DialogContent>
      </Dialog>
      {tab === "messages" && <TripMessages trip={trip} />}
    </div>
  );
}
